'use client'

import { useSyncExternalStore } from 'react'

import {
  APPEARANCE_STORAGE_KEY,
  COLOR_MODE_STORAGE_KEY,
  DEFAULT_APPEARANCE,
  parseAppearance,
  type AppearancePreferences,
} from './preferences'

/**
 * Tiny external store for the appearance preferences. One snapshot object per state so
 * `useSyncExternalStore` can compare by reference; persisted to localStorage on every write
 * and re-read when another tab writes it.
 */
let snapshot: AppearancePreferences | null = null
const listeners = new Set<() => void>()
let watchingStorage = false

const prefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches

const readStorage = (): AppearancePreferences => {
  let stored: Partial<AppearancePreferences> = {}
  try {
    const raw = window.localStorage.getItem(APPEARANCE_STORAGE_KEY)
    if (raw) stored = parseAppearance(JSON.parse(raw))
  } catch {
    // Private mode or a corrupt entry: fall through to the defaults.
  }
  const prefs: AppearancePreferences = { ...DEFAULT_APPEARANCE, ...stored }

  if (stored.theme === undefined) {
    // Before this page existed the sun/moon toggle wrote straight to Chakra's key. A stored mode
    // that differs from the OS was an explicit choice, so honour it; one that matches the OS is
    // indistinguishable from "follow the system", which is the better default.
    try {
      const chakra = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY)
      const os = prefersDark() ? 'dark' : 'light'
      if ((chakra === 'light' || chakra === 'dark') && chakra !== os) prefs.theme = chakra
    } catch {
      // ignore
    }
  }
  return prefs
}

const emit = () => listeners.forEach((listener) => listener())

const getSnapshot = () => {
  if (!snapshot) snapshot = readStorage()
  return snapshot
}

const getServerSnapshot = () => DEFAULT_APPEARANCE

export const subscribeAppearance = (listener: () => void) => {
  listeners.add(listener)
  if (!watchingStorage && typeof window !== 'undefined') {
    watchingStorage = true
    window.addEventListener('storage', (event) => {
      if (event.key === APPEARANCE_STORAGE_KEY || event.key === null) {
        snapshot = null
        emit()
      }
    })
  }
  return () => {
    listeners.delete(listener)
  }
}

export const getAppearance = (): AppearancePreferences =>
  typeof window === 'undefined' ? DEFAULT_APPEARANCE : getSnapshot()

export const setAppearance = (patch: Partial<AppearancePreferences>) => {
  const next = { ...getSnapshot(), ...patch }
  snapshot = next
  try {
    window.localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Storage unavailable: the choice still applies for this session.
  }
  emit()
}

export const resetAppearance = () => setAppearance(DEFAULT_APPEARANCE)

/** The current preferences; renders the defaults on the server and during hydration. */
export const useAppearance = () =>
  useSyncExternalStore(subscribeAppearance, getAppearance, getServerSnapshot)
